import { parse } from './parse';
import type { Expression } from './types';

/**
 * Return the unique dotted paths referenced by a template string, in the
 * order of first occurrence.
 *
 * Only `kind: 'path'` expressions contribute — function-call expressions
 * (e.g. `comparison.isSelected(item.offerId)`) are **excluded**. DB-2.2 will
 * provide its own comparison-aware dependency tracker once function-call
 * resolution lands; for DB-1.2 the parse-level dependency graph is paths
 * only.
 *
 * Used by DB-1.3's DataSourceManager for ADR-10 re-fetch semantics: when a
 * formState key changes, re-fetch every data source whose URL / body / query
 * template references that key.
 *
 * @example
 * extractPaths('{{context.degree}} at {{context.degree}}');
 * // => ['context.degree']  // deduped, order preserved
 *
 * @example
 * extractPaths('{{a}} {{b}} {{a}} {{comparison.isSelected(x)}}');
 * // => ['a', 'b']  // function calls omitted
 */
export function extractPaths(template: string): string[] {
  const seen = new Set<string>();
  const paths: string[] = [];
  const { segments } = parse(template);
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type !== 'expression' || seg.kind !== 'path') {
      continue;
    }
    if (!seen.has(seg.path)) {
      seen.add(seg.path);
      paths.push(seg.path);
    }
  }
  return paths;
}

/**
 * Source names a template reads, taken from the first segment of each path.
 *
 * A quoted key stringifies as `lookup["summary text"]`. Splitting that on
 * `.` does not yield `lookup`, so callers that only need the root should
 * use this instead of {@link extractPaths}.
 */
export function extractRootNames(template: string): string[] {
  const seen = new Set<string>();
  const roots: string[] = [];
  const { segments } = parse(template);
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment === undefined || segment.type !== 'expression') {
      continue;
    }
    collectRootNames(segment, seen, roots);
  }
  return roots;
}

function collectRootNames(
  expression: Expression,
  seen: Set<string>,
  roots: string[],
): void {
  const root = expression.segments[0];
  if (root?.type === 'key' && !seen.has(root.name)) {
    seen.add(root.name);
    roots.push(root.name);
  }
  if (expression.arg !== undefined) {
    collectRootNames(expression.arg, seen, roots);
  }
}
